'use client';

import { useCallback, useEffect, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import DiscussionCard, { type Discussion } from './DiscussionCard';
import DiscussionForm from './DiscussionForm';

/* ── Constants ─────────────────────────────────────────────────────────── */

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'general', label: 'General' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'question', label: 'Question' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'most_commented', label: 'Most discussed' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'solved', label: 'Solved' },
];

/* ── Component ─────────────────────────────────────────────────────────── */

export default function FeedbackBoard() {
  const { user } = useAuth();

  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('newest');

  // Form
  const [showForm, setShowForm] = useState(false);

  const LIMIT = 20;

  const fetchDiscussions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (status) params.set('status', status);
      params.set('sort', sort);
      params.set('page', String(page));
      params.set('limit', String(LIMIT));

      const res = await fetch(`/api/feedback?${params.toString()}`);
      const data = await res.json();
      if (data.ok) {
        setDiscussions(data.discussions);
        setTotal(data.total);
      }
    } catch {
      // Silent fail — show empty state
    } finally {
      setLoading(false);
    }
  }, [category, status, sort, page]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [category, status, sort]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {/* Category pills */}
          <div className="flex gap-1.5">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className="inline-flex h-8 items-center rounded-full px-3 text-xs font-medium transition-colors"
                style={{
                  background:
                    category === cat.value ? 'var(--pub-ember)' : 'var(--pub-surface-card)',
                  color:
                    category === cat.value ? 'var(--color-legacy-white)' : 'var(--pub-text-secondary)',
                  border: `1px solid ${
                    category === cat.value ? 'var(--pub-ember)' : 'var(--pub-border-subtle)'
                  }`,
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-8 rounded-full border bg-[var(--pub-surface-card)] px-3 text-xs font-medium outline-none"
            style={{
              borderColor: 'var(--pub-border-subtle)',
              color: 'var(--pub-text-secondary)',
            }}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-8 rounded-full border bg-[var(--pub-surface-card)] px-3 text-xs font-medium outline-none"
            style={{
              borderColor: 'var(--pub-border-subtle)',
              color: 'var(--pub-text-secondary)',
            }}
            aria-label="Sort discussions"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Create button */}
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="h-9 shrink-0"
          style={{ borderRadius: 'var(--pub-radius-pill)' }}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          New discussion
        </Button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="mb-6">
          <DiscussionForm
            onCreated={() => {
              setShowForm(false);
              fetchDiscussions();
            }}
            onClose={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Discussion list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--pub-text-muted)' }} />
        </div>
      ) : discussions.length === 0 ? (
        <div
          className="rounded-[var(--pub-radius-xl)] p-12 text-center"
          style={{
            background: 'var(--pub-surface-card)',
            border: '1px solid var(--pub-border-subtle)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--pub-text-secondary)' }}>
            No discussions yet. Be the first to share your feedback!
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {discussions.map((d) => (
              <DiscussionCard key={d.id} discussion={d} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ borderRadius: 'var(--pub-radius-pill)' }}
              >
                Previous
              </Button>
              <span
                className="text-sm"
                style={{ color: 'var(--pub-text-secondary)' }}
              >
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ borderRadius: 'var(--pub-radius-pill)' }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
